import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useLenis } from "lenis/react";
import { Mic, Play, User, Clock } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/site/Navbar";
import { Footer } from "../components/site/Footer";
import { Reveal } from "../components/site/Reveal";

const EPISODES = [
  {
    ep: "01",
    title: "In Conversation with Rene Sorola",
    desc: "A Texas A&M (Mays Business School) graduate, Rene leads Rene Sorola Properties — consistently the Met's #1 team in volume and units, ranked among the Houston Business Journal's Top 20 Real Estate Teams every year since 2010, and a member of Gary Keller's Top 100 Agents. His team is built on honesty, integrity and educating every client through the entire process.",
    guest: "Rene Sorola",
    guestRole: "Team Lead of Rene Sorola Properties",
    duration: "— min",
    date: "New Episode",
  },
  {
    ep: "02",
    title: "In Conversation with Jose Berlanga",
    desc: "A Houston developer, serial entrepreneur and author, Jose arrived from Mexico City in the mid-1980s and went on to co-found Tricon Homes and lead ventures including Onyx, Nest Luxury Homes, Houstonian Capital and Citiquest Properties. With 35+ years shaping Houston's urban infill, he authored 'The Business of Home Building' and 'Dirt Rich.'",
    guest: "Jose Berlanga",
    guestRole: "Developer, Entrepreneur & Author",
    duration: "— min",
    date: "New Episode",
  },
];

const GuestAvatar = ({ size = 56 }) => (
  <div
    className="shrink-0 rounded-full border border-gold/40 bg-charcoal/40 flex items-center justify-center"
    style={{ height: size, width: size }}
  >
    <User size={size * 0.42} strokeWidth={1.2} className="text-taupe" />
  </div>
);

export default function Podcast() {
  const lenis = useLenis();

  useEffect(() => {
    lenis?.scrollTo(0, { immediate: true });
  }, [lenis]);

  const notify = (label) =>
    toast("Coming Soon", { description: `${label} — episodes launch with the site. Stay tuned.` });

  return (
    <div className="bg-ink" data-testid="podcast-page">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden pt-40 md:pt-52 pb-20 md:pb-28 border-b border-charcoal/40">
        <img
          src={`${process.env.PUBLIC_URL || ""}/htc-emblem.png`}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute -right-20 -top-10 w-80 md:w-[28rem] opacity-[0.05] select-none"
        />
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <div className="overline mb-8 flex items-center gap-3">
              <Mic size={16} strokeWidth={1.4} className="text-gold" />
              The Houston Title Podcast
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-serif font-light text-ivory leading-[1] tracking-tight text-[clamp(2.5rem,6.5vw,6rem)] max-w-4xl">
              Conversations on trust, property & the art of the close.
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-10 max-w-2xl text-taupe text-base md:text-lg font-light leading-relaxed">
              Hosted by Houston Title Co. — candid conversations with the agents, builders, developers and
              investors shaping Houston real estate. New episodes are on the way.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Featured / next episode */}
      <section className="relative bg-ink py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <div className="overline mb-10 text-taupe">Latest Episode</div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 border border-charcoal/60 bg-card p-8 md:p-12">
              <div className="lg:col-span-4">
                <div className="relative aspect-square overflow-hidden bg-charcoal/40 border border-charcoal/60 flex items-center justify-center">
                  <img
                    src={`${process.env.PUBLIC_URL || ""}/htc-emblem-gold.png`}
                    alt="Houston Title Podcast"
                    className="w-1/2 opacity-80"
                  />
                  <span className="absolute bottom-4 left-4 overline text-[0.55rem] text-taupe/70">
                    Episode Artwork
                  </span>
                </div>
              </div>
              <div className="lg:col-span-8 flex flex-col justify-center">
                <div className="flex items-center gap-5 text-taupe/70 text-[0.72rem] uppercase tracking-[0.2em] mb-6">
                  <span className="font-serif text-gold text-lg not-italic">Ep. 01</span>
                  <span className="inline-flex items-center gap-2"><Clock size={13} strokeWidth={1.4} /> — min</span>
                  <span>New Episode</span>
                </div>
                <h2 className="font-serif font-light text-ivory text-4xl md:text-5xl leading-tight mb-6">
                  In Conversation with Rene Sorola
                </h2>
                <p className="text-taupe font-light leading-relaxed max-w-2xl mb-8">
                  Our debut conversation features Rene Sorola, Team Lead of Rene Sorola Properties at Keller
                  Williams Metropolitan — the Met's #1 team in volume and units and a Houston Business Journal
                  Top 20 team since 2010 — on building a results-driven practice founded on integrity.
                </p>
                <div className="flex items-center gap-5">
                  <GuestAvatar />
                  <div>
                    <div className="overline text-[0.55rem] text-taupe mb-1">Featuring</div>
                    <div className="font-serif text-ivory text-xl">Rene Sorola</div>
                    <div className="text-taupe text-sm font-light">
                      Team Lead of Rene Sorola Properties
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => notify("This episode")}
                  data-testid="podcast-featured-listen"
                  className="group relative mt-10 self-start inline-flex items-center gap-3 overflow-hidden bg-ivory text-ink text-[0.72rem] uppercase tracking-[0.2em] px-8 py-4"
                >
                  <Play size={14} className="relative z-10 fill-ink" />
                  <span className="relative z-10">Listen Soon</span>
                  <span className="absolute inset-0 bg-gold translate-y-full transition-transform duration-500 group-hover:translate-y-0" />
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Episode list */}
      <section className="relative bg-ink pb-28 md:pb-40" data-testid="podcast-episodes">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <div className="flex items-end justify-between mb-12">
              <h2 className="font-serif font-light text-ivory text-4xl md:text-6xl tracking-tight">
                Episodes
              </h2>
              <span className="overline text-taupe/60 hidden md:block">Available Now</span>
            </div>
          </Reveal>

          <div className="border-t border-charcoal/50">
            {EPISODES.map((e, i) => (
              <Reveal key={e.ep} delay={(i % 4) * 0.06}>
                <motion.div
                  whileHover={{ x: 8 }}
                  transition={{ duration: 0.4 }}
                  className="group grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-center py-10 border-b border-charcoal/50"
                  data-testid={`podcast-episode-${e.ep}`}
                >
                  <div className="md:col-span-1 font-serif text-gold text-2xl">{e.ep}</div>

                  <div className="md:col-span-5">
                    <h3 className="font-serif text-2xl md:text-3xl text-ivory font-light mb-3">{e.title}</h3>
                    <p className="text-taupe text-sm font-light leading-relaxed max-w-md">{e.desc}</p>
                  </div>

                  <div className="md:col-span-4 flex items-center gap-4">
                    <GuestAvatar size={48} />
                    <div>
                      <div className="overline text-[0.5rem] text-taupe/70 mb-1">Guest</div>
                      <div className="font-serif text-ivory text-lg leading-tight">{e.guest}</div>
                      <div className="text-taupe text-xs font-light">{e.guestRole}</div>
                    </div>
                  </div>

                  <div className="md:col-span-2 flex md:flex-col md:items-end gap-3 md:gap-2">
                    <span className="inline-flex items-center gap-2 text-taupe/70 text-xs uppercase tracking-[0.15em]">
                      <Clock size={12} strokeWidth={1.4} /> {e.duration}
                    </span>
                    <button
                      onClick={() => notify(`Episode ${e.ep}`)}
                      data-testid={`podcast-listen-${e.ep}`}
                      className="inline-flex items-center gap-2 text-gold text-[0.7rem] uppercase tracking-[0.2em] border-b border-gold/40 hover:border-gold pb-1 transition-colors duration-300"
                    >
                      <Play size={12} className="fill-gold" /> {e.date}
                    </button>
                  </div>
                </motion.div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <p className="mt-16 text-center text-taupe/60 font-light text-sm">
              Two episodes are complete, with more on the way. Streaming links will be added shortly.
            </p>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
